// SuperAdminFinancials — Feb 21, 2026 (P0+P1+P2 rewrite per Randy QA).
//
// Sections (top → bottom):
//   1. Stripe platform dashboard CTA  — links to the test/live balance
//      overview where the $640 in platform-side revenue actually lives.
//   2. This month — 5 KPI cards. Revenue in / Owed to users / Platform keep
//      are CLICKABLE; clicking switches the breakdown panel below.
//   3. Breakdown panel — drilldown for whichever KPI is selected.
//   4. Pending affiliate payouts — sweep queue + per-status totals.
//   5. Pending creator payouts — per-creator breakdown (REPLACES the
//      old all-zeros Liabilities section).
//   6. Recent ledger — last 25 successful transactions (initiated/failed
//      filtered out at the API level).
//   7. LLM burn vs profit — last 30d cost vs platform keep.
//   8. Platform LLM bucket — voucher income + transfers + reservoir.
//
// (Test rev-share simulator removed Feb 21, 2026 — never used.)
import { useEffect, useMemo, useState } from "react";
import {
  CurrencyDollar, TrendUp, Bank, Receipt, Sparkle, Spinner, ArrowDown,
  ArrowUp, Warning, ArrowsClockwise, ArrowSquareOut,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PlatformUsageReservoirPanel from "@/components/PlatformUsageReservoirPanel";
import VoucherRefundsPanel from "@/components/VoucherRefundsPanel";
import FinancialReviewBanner from "@/components/FinancialReviewBanner";
import PaymentsAuditBackfillPanel from "@/components/PaymentsAuditBackfillPanel";
import { AdminMarginTracker } from "@/components/UsageOverview";


const fmt$ = (cents) => {
  const n = (cents || 0) / 100;
  const abs = Math.abs(n);
  const formatted = abs.toLocaleString(undefined, {
    minimumFractionDigits: abs >= 1000 ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return `${n < 0 ? "−" : ""}$${formatted}`;
};

// Friendly label for revenue-by-kind rows. fan_subscription is rendered as
// "Founding-member fan subscriptions" since right now Randy's silo is the
// only one with paying fans — once another creator gets a paying fan we'll
// generalize. The fan-sub line ALSO gets split into "routed to creator" +
// "kept by platform" sub-rows so the $0.15 application-fee math is obvious.
const KIND_LABEL = {
  platform_subscription: "Creator subscription",
  subscription_invoice: "Subscription renewals",
  manual_invoice: "One-off Stripe invoices",
  fan_subscription: "Founding-member fan subscriptions",
  affiliate_gate: "Affiliate gate",
  dfy_purchase: "Done-for-you build",
  tip: "Tip",
  cashout: "Cash-out",
};

const KIND_COLOR = {
  platform_subscription: "#4A5D4E",
  subscription_invoice: "#6B8A6E",
  manual_invoice: "#8A8578",
  fan_subscription: "#7B9E7E",
  affiliate_gate: "#C16E5A",
  dfy_purchase: "#9A7B3F",
  tip: "#E5C77A",
  cashout: "#6B6A68",
};

export default function SuperAdminFinancials() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [sweeping, setSweeping] = useState(false);
  const [retrying, setRetrying] = useState(false);
  // Which KPI's breakdown is currently showing. "revenue" (default),
  // "owed", or "keep".
  const [drill, setDrill] = useState("revenue");

  const load = async () => {
    try {
      const r = await api.get("/admin/financials");
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load financials.");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/admin/financials");
        if (!cancel) setData(r.data);
      } catch (e) {
        if (!cancel) setErr(e.response?.data?.detail || "Failed to load financials.");
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, []);

  const runSweep = async () => {
    setSweeping(true);
    try {
      const r = await api.post("/admin/affiliate-payouts/run");
      const counts = r.data?.counts || {};
      const transferred = counts.transferred || 0;
      const held = counts.held || 0;
      toast.success(
        transferred > 0
          ? `Transferred ${transferred} payout${transferred === 1 ? "" : "s"} to Stripe Express${held ? ` · ${held} held below $5 threshold` : ""}.`
          : held > 0
            ? `${held} payout${held === 1 ? "" : "s"} held below $5 minimum. Nothing transferred yet.`
            : "No pending payouts to sweep."
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sweep failed.");
    } finally { setSweeping(false); }
  };

  // Feb 21, 2026 (late eve) — Retry rows stuck in "failed" status. Resets
  // them to "held" so the next sweep picks them up. Useful after a sweep
  // bug fix (like the test-mode-settling misclassification) so old stuck
  // money isn't permanently written off.
  const retryFailed = async () => {
    setRetrying(true);
    try {
      const r = await api.post("/admin/affiliate-payouts/retry-failed");
      const n = r.data?.reset_count || 0;
      toast.success(
        n > 0
          ? `Reset ${n} failed row${n === 1 ? "" : "s"} → held. Next sweep will retry.`
          : "Nothing in failed status to retry."
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed.");
    } finally { setRetrying(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#6B6A68]" data-testid="financials-loading">
        <Spinner size={14} className="animate-spin" /> Crunching the books…
      </div>
    );
  }
  if (err) {
    return (
      <div className="text-sm text-[#7A3A2A] bg-[#FDF1ED] border border-[#E5BBA8] rounded-md p-4">{err}</div>
    );
  }
  if (!data) return null;

  const tm = data.this_month;
  const llm = data.llm_burn;
  const pp = data.pending_payouts || { by_status: {}, total_cents: 0 };
  const pcp = data.pending_creator_payouts || [];

  return (
    <div className="space-y-10" data-testid="financials-panel">
      {/* Oct 3 2026 (Randy, audit §6.7) — the clock, above everything. An
          evidence deadline must not live only at the bottom of the page. */}
      <FinancialReviewBanner />

      {/* SECTION 1 — Stripe platform dashboard CTA ======================= */}
      <PlatformDashboardCard
        url={data.platform_dashboard_url}
        connectedUrl={data.connected_accounts_url}
        mode={data.stripe_mode}
      />

      {/* SECTION 2 — This month at a glance ============================== */}
      <section data-testid="financials-this-month">
        <div className="flex items-baseline gap-3 mb-4 pb-2 border-b hairline">
          <h2 className="font-serif-display text-2xl text-[#1C1B1A]">This month</h2>
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">
            since {new Date(data.month_start).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
          </span>
          <span className="ml-auto text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">
            click a card to drill in
          </span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <KpiCard
            icon={CurrencyDollar} testid="kpi-revenue-in"
            label="Revenue in" sublabel="all successful charges"
            value={fmt$(tm.revenue_in_cents)} accent="#4A5D4E"
            active={drill === "revenue"}
            onClick={() => setDrill("revenue")}
          />
          <KpiCard
            icon={ArrowDown} testid="kpi-owed"
            label="Owed to users" sublabel="this month's rev-share + bounties"
            value={fmt$(tm.owed_to_affiliates_cents)} accent="#C16E5A"
            active={drill === "owed"}
            onClick={() => setDrill("owed")}
          />
          <KpiCard
            icon={TrendUp} testid="kpi-platform-keep"
            label="Platform keep" sublabel="fees collected, before Stripe's cut"
            value={fmt$(tm.platform_keep_cents)} accent="#1C1B1A"
            active={drill === "keep"}
            onClick={() => setDrill("keep")}
          />
          <KpiCard
            icon={Bank} testid="kpi-cashed-out"
            label="Cashed out" sublabel={`${tm.cashed_out_count} withdrawals`}
            value={fmt$(tm.cashed_out_cents)} accent="#6B6A68"
          />
          {/* Sep 25, 2026 (Randy) — this is the primary card now. "Platform
              keep" is the number BEFORE Stripe is paid, so leaving it as the
              biggest figure on the page put the optimistic number where the
              eye lands and the true one at the far right. */}
          <KpiCard
            icon={Sparkle} testid="kpi-net-position"
            label="Net platform position" sublabel="fees collected minus Stripe fees paid"
            value={fmt$(tm.net_platform_position_cents)}
            accent={(tm.net_platform_position_cents ?? 0) >= 0 ? "#2F6B3A" : "#7A3A2A"}
            big
            active={drill === "position"}
            onClick={() => setDrill("position")}
          />
        </div>

        {/* The whole story on one always-visible line, so the sign of the
            position never has to be reconstructed from the drill-down. */}
        <PositionStrip tm={tm} onOpen={() => setDrill("position")} />

        {/* Single panel that morphs based on the selected KPI. Same vertical
            footprint regardless of which drill is active. Long lists scroll. */}
        <BreakdownPanel drill={drill} tm={tm} />
      </section>

      {/* SECTION 4 — Pending affiliate payouts =========================== */}
      <section data-testid="financials-pending-payouts">
        <div className="flex items-baseline gap-3 mb-4 pb-2 border-b hairline">
          <h2 className="font-serif-display text-2xl text-[#1C1B1A]">Pending Partner payouts</h2>
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">
            queued for Stripe Express transfer
          </span>
          <button
            onClick={runSweep}
            disabled={sweeping}
            data-testid="financials-run-sweep-btn"
            className="ml-auto text-xs px-3 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5 disabled:opacity-50"
            title="Triggers the Partner payout sweep immediately instead of waiting for the hourly cron."
          >
            <ArrowsClockwise size={12} weight="bold" className={sweeping ? "animate-spin" : ""} />
            {sweeping ? "Sweeping…" : "Run payouts now"}
          </button>
          {/* Retry-failed CTA — only shows when there's actually something
              stuck in "failed". Pulls failed_count from the financials
              endpoint. Resets those rows to "held" so the next sweep
              picks them up. */}
          {(pp.failed_count || 0) > 0 && (
            <button
              onClick={retryFailed}
              disabled={retrying}
              data-testid="financials-retry-failed-btn"
              className="text-xs px-3 py-1.5 rounded-md border hairline bg-white text-[#1C1B1A] hover:border-[#C16E5A] hover:text-[#C16E5A] flex items-center gap-1.5 disabled:opacity-50"
              title={`Reset ${pp.failed_count} failed row${pp.failed_count === 1 ? "" : "s"} (${fmt$(pp.failed_total_cents)}) so the next sweep retries them.`}
            >
              <Warning size={12} weight="duotone" className={retrying ? "animate-pulse" : ""} />
              {retrying ? "Resetting…" : `Retry ${pp.failed_count} failed`}
            </button>
          )}
        </div>
        <div className="bg-white border hairline rounded-lg p-5">
          <div className="flex items-baseline gap-3 mb-3">
            <div className="font-serif-display text-3xl tabular-nums text-[#1C1B1A]" data-testid="pending-payouts-total">
              {fmt$(pp.total_cents)}
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">
              waiting to transfer
            </div>
          </div>
          {Object.keys(pp.by_status).length === 0 ? (
            <div className="text-sm text-[#6B6A68] italic">
              No payouts queued. Once a Partner earns enough to clear the $5 minimum, the next sweep transfers it to their Stripe Express.
            </div>
          ) : (
            <div className="space-y-1.5">
              {Object.entries(pp.by_status).map(([status, v]) => (
                <div key={status} className="flex items-center gap-3 text-xs" data-testid={`pending-status-${status}`}>
                  <StatusPill status={status} />
                  <span className="text-[#1C1B1A]">{v.count} row{v.count === 1 ? "" : "s"}</span>
                  <span className="flex-1" />
                  <span className="font-mono tabular-nums text-[#1C1B1A]">{fmt$(v.total_cents)}</span>
                </div>
              ))}
            </div>
          )}
          <div className="mt-3 text-[11px] text-[#6B6A68]">
            <strong>earned</strong> = newly written rev-share, ready to sweep ·{" "}
            <strong>held</strong> = Partner's total is below the $5 minimum, accumulating ·{" "}
            <strong>pending</strong> = sweep started but Stripe hasn&apos;t confirmed yet
          </div>
        </div>
      </section>

      {/* SECTION 5 — Pending creator payouts (replaces Liabilities) ===== */}
      <section data-testid="financials-pending-creators">
        <div className="flex items-baseline gap-3 mb-4 pb-2 border-b hairline">
          <h2 className="font-serif-display text-2xl text-[#1C1B1A]">Pending creator payouts</h2>
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">
            who's owed across earned · held · pending
          </span>
        </div>
        {pcp.length === 0 ? (
          <div className="bg-white border hairline rounded-lg p-8 text-center text-sm text-[#6B6A68] italic">
            No creators currently owed anything. As Partner rev-share is generated, recipients appear here grouped by total owed.
          </div>
        ) : (
          <div className="bg-white border hairline rounded-lg divide-y hairline max-h-[320px] overflow-y-auto">
            {pcp.map((row) => (
              <div key={row.user_id} className="px-4 py-3 flex items-center gap-3 text-xs" data-testid={`pcp-row-${row.user_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-[#1C1B1A] truncate">
                    {row.name || row.email.split("@")[0]}
                    <span className="text-[#6B6A68] ml-2">· {row.email}</span>
                  </div>
                  <div className="text-[10px] text-[#6B6A68] mt-0.5">
                    {row.row_count} row{row.row_count === 1 ? "" : "s"}
                    {row.oldest_at && <> · oldest {new Date(row.oldest_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {(row.statuses || []).map((s) => <StatusPill key={s} status={s} />)}
                </div>
                <div className="font-mono tabular-nums text-[#1C1B1A] w-24 text-right">{fmt$(row.total_cents)}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* SECTION 6 — Recent ledger ======================================= */}
      <section data-testid="financials-ledger">
        <div className="flex items-baseline gap-3 mb-4 pb-2 border-b hairline">
          <h2 className="font-serif-display text-2xl text-[#1C1B1A]">Recent ledger</h2>
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">
            last 25 successful transactions
          </span>
        </div>
        {data.recent_ledger.length === 0 ? (
          <div className="bg-white border hairline rounded-lg p-8 text-center text-sm text-[#6B6A68] italic">
            No transactions yet. Once Stripe webhooks fire (subscriptions, gates, tips, cash-outs) they'll all land here.
          </div>
        ) : (
          <div className="bg-white border hairline rounded-lg divide-y hairline" data-testid="ledger-rows">
            {data.recent_ledger.map((r) => <LedgerRow key={r.id} row={r} />)}
          </div>
        )}
      </section>

      {/* SECTION 7 — LLM burn vs profit ================================== */}
      <section data-testid="financials-llm-burn">
        <div className="flex items-baseline gap-3 mb-4 pb-2 border-b hairline">
          <h2 className="font-serif-display text-2xl text-[#1C1B1A]">LLM burn vs profit</h2>
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">last 30 days</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <KpiCard
            icon={Receipt} testid="llm-30d"
            label="LLM cost — last 30 days"
            sublabel={`${(llm.billable_calls ?? 0).toLocaleString()} billable of ${llm.calls_count.toLocaleString()} calls · ${fmt$(llm.platform_key_cents)} on our key`}
            value={fmt$(llm.last_30d_cents)} accent="#6B6A68"
          />
          <KpiCard
            icon={ArrowUp} testid="llm-projected-month"
            label="Projected this month · our key"
            sublabel="platform-funded burn scaled to current month length"
            value={fmt$(llm.projected_month_cents)} accent="#6B6A68"
          />
          <KpiCard
            icon={Sparkle} testid="llm-true-net"
            label={llm.net_basis === "actual" ? "True net profit" : "Estimated net"}
            sublabel={llm.net_basis === "actual"
              ? "keep after Stripe, minus projected platform-funded LLM"
              : llm.net_basis === "partial"
                ? "part of Stripe's cost is estimated — not a verified figure"
                : "Stripe's cost could not be read — estimate only"}
            value={fmt$(llm.net_basis === "actual"
              ? llm.true_net_profit_cents
              : llm.estimated_net_profit_cents)}
            accent={(llm.net_basis === "actual"
              ? llm.true_net_profit_cents
              : llm.estimated_net_profit_cents) >= 0 ? "#2F6B3A" : "#7A3A2A"}
            big
          />
        </div>

        {/* Per-provider rows — Jun 2026 (Randy) */}
        <LlmProviderRows
          rows={llm.by_provider}
          revenueCents={llm.platform_key_cents}
          costCents={llm.provider_cost_cents}
          byokCents={llm.byok_cents}
        />

        <div className="mt-3 text-[11px] text-[#6B6A68] italic">
          Top up the platform LLM key from{" "}
          <span className="text-[#1C1B1A]">Operations → Key Balance</span>{" "}
          when the projected month's burn approaches your remaining key budget.
        </div>
      </section>

      <div className="text-[10px] text-[#6B6A68] text-right pt-4">
        as of {new Date(data.as_of).toLocaleString()}
      </div>

      {/* Platform LLM margin — moved here from the Usage Overview window
          (Jun 2026, Randy). Sits between LLM burn vs profit and the Reservoir. */}
      <section className="mt-2" data-testid="financials-platform-llm-margin">
        <AdminMarginTracker days={30} />
      </section>

      {/* SECTION 8 — Platform Usage Reservoir (live Stripe tracker) =====
          Jun 2026 (Randy) — voucher exposure rides in from /admin/financials
          (`platform_llm_bucket`) rather than a second endpoint, so the
          reservoir panel stays a single fetch. */}
      <PlatformUsageReservoirPanel
        exposure={data.platform_llm_bucket?.voucher_exposure}
        liabilityCents={data.platform_llm_bucket?.voucher_liability_cents}
      />
      {/* SECTION 9 — Voucher refunds, disputes & financial review.
          Jun 2026 (Randy pt-voucher-lots Pass 3) — the review queue was
          API-only until now, which meant money needing a human decision was
          invisible in the app. */}
      <VoucherRefundsPanel />
      {/* SECTION 10 — One-off history repair for the Sep 2026 payments audit.
          Sep 2026 (Randy) — was a shell script, which a deployed production app
          gives you no way to run. Same code, dry-run first. */}
      <PaymentsAuditBackfillPanel />
      {/* Go-Live wizard + Danger Zone moved to the dedicated "Go Live Wizard"
          tab (Jun 2026, Randy) so the finances aren't muddled. */}
    </div>
  );
}

// ============== Stripe Platform dashboard CTA card ====================
function PlatformDashboardCard({ url, connectedUrl, mode }) {
  const isTest = mode === "test";
  // Feb 21, 2026 (late eve) — embed recent platform payouts inline so the
  // super admin can verify at-a-glance which payouts actually swept to the
  // bank, without having to click out to Stripe.
  const [payouts, setPayouts] = useState(null);
  const [balance, setBalance] = useState(null);
  const [payoutsErr, setPayoutsErr] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/admin/stripe-platform-payouts?limit=5");
        if (cancelled) return;
        if (r.data?.ok) {
          setPayouts(r.data.payouts || []);
          setBalance(r.data.balance || null);
        } else setPayoutsErr(r.data?.error || "Couldn't reach Stripe.");
      } catch (e) {
        if (!cancelled) setPayoutsErr(e.response?.data?.detail || "Couldn't reach Stripe.");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <section data-testid="platform-stripe-cta">
      <div className="bg-gradient-to-br from-[#1C1B1A] to-[#3A4A3E] text-[#FDFCF8] rounded-xl p-5 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] opacity-70">
              Where the platform-side money lives
            </div>
            <div className="font-serif-display text-xl leading-tight mt-1">
              Stripe platform dashboard{" "}
              {isTest && <span className="text-[10px] uppercase tracking-[0.18em] bg-[#C16E5A] text-white px-1.5 py-0.5 rounded ml-1.5 align-middle">test mode</span>}
            </div>
            <div className="text-[11px] opacity-75 mt-1 max-w-2xl">
              Creator Pro purchases + creator subscriptions land in your platform Stripe balance.{" "}
              {isTest && <span className="opacity-90"><strong>In test mode, Stripe auto-pays out the balance daily to a virtual test bank</strong> — so the balance page reads $0 even when hundreds of dollars hit. </span>}
              This dashboard shows the actual <strong>payout history</strong> + where to configure your real payout schedule + destination bank for live mode.
            </div>
          </div>
          {/* Feb 21, 2026 (late eve, 2nd pass) — both buttons stack vertically
              on the right rail. Both URLs include the platform acct_xxx
              prefix so Stripe force-routes to "My Silo sandbox" specifically. */}
          <div className="shrink-0 flex flex-col gap-2">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="platform-stripe-link"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-[#FDFCF8] text-[#1C1B1A] text-sm font-medium hover:bg-white"
            >
              Open platform dashboard <ArrowSquareOut size={13} weight="bold" />
            </a>
            <a
              href={connectedUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="connected-accounts-link"
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-white/30 text-[#FDFCF8] text-xs hover:bg-white/10"
              title="See each creator's Stripe Express account (their charges, balance, payouts to their bank)"
            >
              Connected accounts <ArrowSquareOut size={11} weight="bold" />
            </a>
          </div>
        </div>

        {/* Recent platform payouts — embedded so Randy can verify without
            clicking out. Falls back gracefully if Stripe is unreachable. */}
        <div className="mt-4 pt-4 border-t border-white/15" data-testid="platform-recent-payouts">
          {/* Live balance row (Feb 21, 2026 late eve) — what's in the bank,
              what's about to ship. Available is what would pay out today;
              pending is what's still settling from recent charges. */}
          {balance && (
            <div className="mb-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px]" data-testid="platform-balance-row">
              {(() => {
                // Mar 2026 — match the PUR clamp. Treat the WHOLE balance
                // row as fee residue when |total| <= $5 AND either side
                // has fee-residue magnitude. This catches the post-nuke
                // pattern where available reads -$1.75 and pending reads
                // +$1.76 (net ~$0.01, but both individual lines look
                // ugly). Real number stays one hover away.
                const av = balance.available_cents || 0;
                const pe = balance.pending_cents || 0;
                const total = av + pe;
                const isFeeResidue = Math.abs(total) <= 500 && (Math.abs(av) <= 500 || Math.abs(pe) <= 500);
                const avDisp = isFeeResidue ? "~$0" : fmt$(av);
                const peDisp = isFeeResidue ? "~$0" : fmt$(pe);
                const tooltip = isFeeResidue
                  ? `Real available ${fmt$(av)} · real pending ${fmt$(pe)}. Stripe doesn't refund processing fees, so test refunds leave residue. Net is ${fmt$(total)} — essentially zero.`
                  : undefined;
                return (
                  <>
                    <div title={tooltip}>
                      <span className="opacity-60 uppercase tracking-[0.18em] text-[9px] mr-1.5">Available</span>
                      <span className="font-mono tabular-nums">{avDisp}</span>
                    </div>
                    <div title={tooltip}>
                      <span className="opacity-60 uppercase tracking-[0.18em] text-[9px] mr-1.5">Stripe pending (settling)</span>
                      <span className="font-mono tabular-nums">{peDisp}</span>
                    </div>
                    <div className="opacity-50 text-[10px] italic">
                      Stripe pending = your charges still settling (NOT creator payouts owed) · available = ready to pay out
                    </div>
                  </>
                );
              })()}
            </div>
          )}
          <div className="text-[10px] uppercase tracking-[0.22em] opacity-70 mb-2">
            Recent platform payouts
          </div>
          {payouts === null && !payoutsErr ? (
            <div className="text-[11px] opacity-75 flex items-center gap-2">
              <Spinner size={11} className="animate-spin" /> Fetching from Stripe…
            </div>
          ) : payoutsErr ? (
            <div className="text-[11px] opacity-75 italic">
              Couldn’t fetch payouts ({payoutsErr}). Open the dashboard to view history.
            </div>
          ) : payouts.length === 0 ? (
            <div className="text-[11px] opacity-75 italic">
              No payouts yet. Once Stripe runs its first daily sweep, the last 5 will land here.
            </div>
          ) : (
            <div className="space-y-1.5">
              {payouts.map((p) => <PayoutRow key={p.id} p={p} />)}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function PayoutRow({ p }) {
  const statusColor = {
    paid:        "bg-[#2F6B3A] text-white",
    in_transit:  "bg-[#8A5A00] text-white",
    pending:     "bg-[#6B5A2D] text-white",
    failed:      "bg-[#7A3A2A] text-white",
    canceled:    "bg-[#6B6A68] text-white",
  }[p.status] || "bg-white/20 text-white";
  const created = p.created ? new Date(p.created * 1000) : null;
  const arrival = p.arrival_date ? new Date(p.arrival_date * 1000) : null;
  return (
    <div className="flex items-center gap-3 text-[11px]" data-testid={`platform-payout-${p.id}`}>
      <span className={`text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded ${statusColor}`}>
        {p.status}
      </span>
      <span className="font-mono tabular-nums">
        ${(p.amount_cents / 100).toFixed(2)}
      </span>
      <span className="opacity-60">
        {created ? created.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}
        {arrival && p.status === "paid" && <> · arrived {arrival.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</>}
      </span>
      <span className="flex-1" />
      <span className="opacity-50 font-mono text-[10px] truncate max-w-[140px]" title={p.destination}>
        → {p.destination}
      </span>
    </div>
  );
}

// ============== Breakdown panel (KPI drilldown) =======================
function BreakdownPanel({ drill, tm }) {
  if (drill === "owed") return <OwedBreakdown tm={tm} />;
  if (drill === "keep") return <KeepBreakdown tm={tm} />;
  if (drill === "position") return <PositionBreakdown tm={tm} />;
  return <RevenueBreakdown tm={tm} />;
}

// Sep 25, 2026 (Randy — "your current dashboard is encouraging bad decisions").
// The card this replaces read "True net profit $0.70" for a month in which
// Stripe had taken $1.61. Platform fees collected and Stripe fees paid are now
// shown as two separate lines with the real position underneath, and the
// break-even is stated outright, because that is the number the pricing
// decision actually turns on.
const STRIPE_COST_SOURCE_LABEL = {
  stripe_actual: "read from Stripe",
  stripe_actual_partial: "read from Stripe, part estimated",
  estimated: "estimated — Stripe could not be reached",
  unavailable: "unavailable",
};

// Sep 25, 2026 (Randy) — the always-visible equation. He could read the four
// numbers only by clicking the fifth KPI card, so a negative month never
// explained itself. Renders the terms that MAKE the KPI (fees collected minus
// the per-charge Stripe fees on those same charges), then the fees Stripe bills
// monthly rather than per charge as a separate trailing term — those are NOT
// inside `net_platform_position_cents` and the line says so instead of
// pretending the equals sign covers them.
function PositionStrip({ tm, onOpen }) {
  const sc = tm.stripe_cost || {};
  const pos = tm.net_platform_position_cents ?? 0;
  const billed = sc.billed_fees_cents || 0;
  const posColor = pos >= 0 ? "#2F6B3A" : "#7A3A2A";
  const Term = ({ op, label, value, tone }) => (
    <span className="inline-flex items-baseline gap-1.5">
      {op && <span className="text-[#A8A49B] font-mono">{op}</span>}
      <span className="text-[#6B6A68]">{label}</span>
      <span className="font-mono tabular-nums font-medium" style={{ color: tone }}>{fmt$(value)}</span>
    </span>
  );
  return (
    <button type="button" onClick={onOpen} data-testid="position-strip"
      title="Open the full breakdown"
      className="mt-3 w-full text-left bg-white border hairline rounded-lg px-4 py-3 hover:border-[#4A5D4E] transition-colors">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1.5 text-[11px]">
        <Term label="Platform fees collected" value={tm.application_fee_cents} tone="#2F6B3A" />
        <Term op="−" label="Stripe processing on those charges" value={sc.supporter_cents} tone="#7A3A2A" />
        <span className="text-[#A8A49B] font-mono">=</span>
        <span className="inline-flex items-baseline gap-1.5">
          <span className="text-[#1C1B1A] font-medium">Net platform position</span>
          <span className="font-mono tabular-nums font-medium text-[13px]" style={{ color: posColor }}
            data-testid="position-strip-net">{fmt$(pos)}</span>
        </span>
        <span className="ml-auto text-[10px] uppercase tracking-[0.18em] text-[#A8A49B]">
          {STRIPE_COST_SOURCE_LABEL[sc.source] || "unknown source"}
        </span>
      </div>
      {billed > 0 && (
        <div className="mt-1.5 pt-1.5 border-t hairline flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[10px] text-[#6B6A68]"
          data-testid="position-strip-billed">
          <Term op="+" label="Stripe Billing & account fees, billed monthly rather than per charge"
            value={billed} tone="#7A3A2A" />
          <span className="text-[#A8A49B] font-mono">→</span>
          <span>
            position including them{" "}
            <span className="font-mono tabular-nums font-medium"
              style={{ color: pos - billed >= 0 ? "#2F6B3A" : "#7A3A2A" }}
              data-testid="position-strip-with-billed">{fmt$(pos - billed)}</span>
          </span>
        </div>
      )}
    </button>
  );
}

function PositionBreakdown({ tm }) {
  const sc = tm.stripe_cost || {};
  const pos = tm.net_platform_position_cents || 0;
  const afterStripe = tm.platform_keep_after_stripe_cents || 0;
  const legacyCount = tm.legacy_charge_count || 0;
  const feeCount = tm.supporter_fee_charge_count || 0;
  const chargeCount = tm.supporter_charge_count || 0;
  const isActual = sc.source === "stripe_actual";
  let source = STRIPE_COST_SOURCE_LABEL[sc.source] || "unknown source";
  if (sc.source === "stripe_actual_partial") {
    source = `${sc.supporter_matched} read from Stripe, ${sc.supporter_estimated} estimated at ${(sc.estimate_rate_bps / 100).toFixed(1)}% + ${fmt$(sc.estimate_fixed_cents)}`;
  }
  return (
    <div className="mt-5 bg-white border hairline rounded-lg p-5 space-y-2 text-xs" data-testid="breakdown-position">
      <div className="text-xs uppercase tracking-[0.22em] text-[#6B6A68] mb-1">
        Net platform position · supporter payments
      </div>
      <KeepRow label={`Gross supporter volume · ${tm.supporter_charge_count} charge${tm.supporter_charge_count === 1 ? "" : "s"}`
        + (feeCount && legacyCount ? ` · ${feeCount} with a service fee, ${legacyCount} legacy` : "")}
        value={tm.supporter_gross_cents} sign="" />
      <KeepRow label="Creator amount · left the platform at charge time"
        value={tm.supporter_creator_amount_cents} sign="−" />
      <KeepRow label={`Platform fees collected · ${(tm.fan_cut_bps_used / 100).toFixed(1)}% application fee`}
        value={tm.application_fee_cents} sign="+" />
      <KeepRow label={`Stripe processing fees on those charges · ${source}`}
        value={sc.supporter_cents} sign="−" tone="alert" />
      <div className="pt-2 mt-2 border-t hairline flex items-center gap-3">
        <div className="flex-1 text-[#1C1B1A] font-medium">Net platform position</div>
        <div className="font-mono tabular-nums font-medium"
          style={{ color: pos >= 0 ? "#2F6B3A" : "#7A3A2A" }}
          data-testid="position-net">{fmt$(pos)}</div>
      </div>
      {pos < 0 && (
        <div className="mt-2 rounded-md bg-[#FBF0EC] border border-[#E8CEC4] p-3 text-[11px] text-[#7A3A2A] leading-relaxed"
          data-testid="position-below-breakeven">
          {/* Sep 25, 2026 — this paragraph used to state the $14.29 break-even
              as the whole truth, which describes the economics BEFORE the
              Supporter Service Fee: at a flat {fee}% against 2.9% + 30¢ the
              platform lost money on every small payment. Under Model A the
              supporter pays that cost, so a fee-bearing charge clears itself at
              any price and the only thing that can hold the month negative is
              volume taken before the fee existed. The card now says which of
              the two it is looking at. */}
          Supporter payments cost more than they earned this month.
          {legacyCount > 0 ? (
            <>
              {" "}
              <strong>{legacyCount} of this month&apos;s {chargeCount} charge{chargeCount === 1 ? "" : "s"} predate{legacyCount === 1 ? "s" : ""} the
              supporter service fee.</strong> On those, the platform absorbed the processing cost out of its
              own {(tm.fan_cut_bps_used / 100).toFixed(1)}%, which does not cover
              {" "}{(sc.estimate_rate_bps / 100).toFixed(1)}% + {fmt$(sc.estimate_fixed_cents)} until the payment reaches
              {" "}<strong>$14.29</strong> — so more of that kind of volume makes this worse, not better.
              {feeCount > 0 && (
                <>
                  {" "}
                  The {feeCount} charge{feeCount === 1 ? "" : "s"} that carried the service fee
                  {feeCount === 1 ? " is" : " are"} priced to cover {feeCount === 1 ? "its" : "their"} own
                  processing at every amount, so {feeCount === 1 ? "it is" : "they are"} not what is holding
                  this negative.
                </>
              )}
            </>
          ) : (
            <>
              {" "}
              <strong>Every charge this month carried the supporter service fee</strong>, so the pricing
              model is not the cause — a fee-bearing charge covers its own processing at any amount. What
              Stripe actually took came in above the {(sc.estimate_rate_bps / 100).toFixed(1)}% +
              {" "}{fmt$(sc.estimate_fixed_cents)} the fee is priced from, so the two estimate dials are
              what to look at, not the model.
            </>
          )}
        </div>
      )}
      <div className="pt-3 mt-3 border-t hairline space-y-2">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">
          Whole platform, same month
        </div>
        <KeepRow label="Platform keep · fees + direct revenue − rev-share" value={tm.platform_keep_cents} sign="+" />
        <KeepRow label="All Stripe processing fees · every platform charge"
          value={(sc.all_platform_cents || 0) - (sc.connect_costs_cents || 0)} sign="−" tone="alert" />
        {(sc.billed_fee_items || []).map((it) => (
          <KeepRow key={it.description} label={`${it.description} · billed by Stripe, not per charge`}
            value={it.cents} sign="−" tone="alert" />
        ))}
        {(sc.payout_fees_cents || 0) > 0 && (
          <KeepRow label="Payout fees · 0.25% + $0.25 per payout sent"
            value={sc.payout_fees_cents} sign="−" tone="alert" />
        )}
        {!sc.connect_costs_available && (
          <KeepRow label="Connect costs · unavailable (Stripe could not be read)"
            value={0} sign="−" tone="alert" />
        )}
        <div className="pt-2 border-t hairline flex items-center gap-3">
          <div className="flex-1 text-[#1C1B1A] font-medium">Platform keep after Stripe</div>
          <div className="font-mono tabular-nums font-medium"
            style={{ color: afterStripe >= 0 ? "#2F6B3A" : "#7A3A2A" }}
            data-testid="position-after-stripe">{fmt$(afterStripe)}</div>
        </div>
        {sc.connect_costs_available && (sc.connect_costs_cents || 0) === 0 && (
          <div className="text-[10px] text-[#6B6A68]">
            No payout or per-active-account fees this month — Stripe bills the $2 per-active-account fee
            and the 0.25% + $0.25 payout fee only in months where a creator is actually paid out.
            Everything Stripe did charge is itemised above, so this total ties to your Stripe balance.
          </div>
        )}
        {!isActual && (
          <div className="text-[10px] text-[#7A3A2A]" data-testid="position-estimate-warning">
            Part of this month's cost is estimated, so the figure below the LLM section is published as an
            estimated net, not True net profit.
          </div>
        )}
        {sc.truncated && (
          <div className="text-[10px] text-[#7A3A2A]">
            Stripe returned more than 2,000 transactions this month — the cost above is a floor.
          </div>
        )}
      </div>
    </div>
  );
}

function RevenueBreakdown({ tm }) {
  // Build flat rows for the kinds present. Render fan_subscription as a
  // PARENT row with two indented child rows (routed-to-creator + kept-by-
  // platform) so the $0.15 application-fee math is obvious at a glance.
  const kinds = Object.entries(tm.revenue_by_kind || {});
  const hasFan = !!tm.revenue_by_kind?.fan_subscription;
  const hasAnything = kinds.length > 0 || tm.application_fee_cents > 0;
  return (
    <div className="mt-5 bg-white border hairline rounded-lg p-5" data-testid="breakdown-revenue">
      <div className="text-xs uppercase tracking-[0.22em] text-[#6B6A68] mb-3">Revenue breakdown</div>
      {!hasAnything ? (
        <div className="text-sm text-[#6B6A68] italic">No revenue yet this month.</div>
      ) : (
        <div className="space-y-2">
          {kinds.map(([k, v]) => {
            const isFan = k === "fan_subscription";
            const isRenewal = k === "subscription_invoice" || k === "manual_invoice";
            return (
              <div key={k} data-testid={`rev-kind-${k}`}>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ background: KIND_COLOR[k] || "#6B6A68" }} />
                  <div className="text-xs flex-1 min-w-0">
                    <span className="text-[#1C1B1A]">{KIND_LABEL[k] || k}</span>
                    <span className="text-[#6B6A68] ml-2">· {v.count} {v.count === 1 ? "txn" : "txns"}</span>
                  </div>
                  <div className="text-xs font-mono tabular-nums text-[#1C1B1A]">{fmt$(v.total_cents)}</div>
                </div>
                {/* Sep 25, 2026 — renewals used to be one undifferentiated row, and
                    the whole gross of it was booked as platform revenue. A fan
                    renewal is ~5% ours; the creator's own plan is 100% ours. */}
                {isRenewal && (
                  <div className="ml-5 mt-1 space-y-0.5 text-[11px] text-[#6B6A68]"
                    data-testid="rev-renewal-split">
                    <div className="flex items-center gap-2">
                      <span className="opacity-60">└</span>
                      <span>creator's own plan · {tm.platform_renewal_count} · 100% ours</span>
                      <span className="flex-1" />
                      <span className="font-mono tabular-nums">{fmt$(tm.platform_renewal_gross_cents)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="opacity-60">└</span>
                      <span>fan subscription renewals · {tm.fan_renewal_count} · we keep {(tm.fan_cut_bps_used / 100).toFixed(1)}%</span>
                      <span className="flex-1" />
                      <span className="font-mono tabular-nums">
                        {fmt$(tm.fan_renewal_gross_cents)}
                        <span className="text-[#2F6B3A]"> → {fmt$(tm.fan_renewal_app_fee_cents)}</span>
                      </span>
                    </div>
                    {tm.unattributed_renewal_count > 0 && (
                      <div className="flex items-center gap-2 text-[#7A3A2A]">
                        <span className="opacity-60">└</span>
                        <span>unattributable · {tm.unattributed_renewal_count} · real subscription id, matches nothing here · counted as neither</span>
                        <span className="flex-1" />
                        <span className="font-mono tabular-nums">{fmt$(tm.unattributed_renewal_gross_cents)}</span>
                      </div>
                    )}
                    {/* Oct 3 2026 (Randy, F7 decision 4C) — never silence. Each
                        unresolved renewal is asked about in Stripe once, and
                        whatever is left says WHY it could not be attributed
                        rather than vanishing from revenue. */}
                    {(tm.unattributed_renewal_detail || []).length > 0 && (
                      <div className="mt-1.5 pl-3 border-l-2 border-[#E5BBA8] space-y-1"
                        data-testid="rev-unattributed-detail">
                        <div className="text-[#7A3A2A]">
                          Needs attribution — revenue is counted NOWHERE until someone says whose
                          it is, because booking a fan&apos;s renewal as platform income would take
                          ~95% of a creator&apos;s money:
                        </div>
                        {tm.unattributed_renewal_detail.map((u) => (
                          <div key={u.stripe_subscription_id}
                            className="text-[10px] text-[#6B6A68] leading-relaxed">
                            <span className="text-[#1C1B1A]">{u.why}</span>
                            {u.customer && <span className="font-mono opacity-60"> · {u.customer}</span>}
                            {u.livemode === false && " · test mode"}
                            <span className="font-mono opacity-60"> · {u.stripe_subscription_id}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Sep 25, 2026 — these used to sit in "unattributable" with a raw
                        id and a Stripe request id. They are one-off invoices with no
                        subscription: not renewals at all. */}
                    {tm.manual_invoice_count > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="opacity-60">└</span>
                        <span>
                          one-off Stripe invoices · {tm.manual_invoice_count} · no subscription
                          {(tm.manual_invoice_gross_cents || 0) === 0 && " · nothing collected"}
                        </span>
                        <span className="flex-1" />
                        <span className="font-mono tabular-nums">{fmt$(tm.manual_invoice_gross_cents)}</span>
                      </div>
                    )}
                    {(tm.zero_amount_renewals || []).length > 0 && (
                      <div className="mt-1.5 pl-3 border-l-2 border-[#E4E1D9] space-y-1"
                        data-testid="rev-zero-renewals">
                        <div className="text-[#6B6A68]">
                          {tm.zero_amount_renewals.length} collected $0.00 — Stripe settles and
                          records an invoice even when there is nothing to charge, so the receipt
                          is real and the money is genuinely zero:
                        </div>
                        {tm.zero_amount_renewals.map((z) => (
                          <div key={z.id} className="text-[10px] text-[#6B6A68] leading-relaxed">
                            <span className="text-[#1C1B1A]">
                              {z.belongs_to === "manual_invoice"
                                ? "One-off invoice, no subscription"
                                : z.belongs_to === "unresolved_subscription"
                                  ? "Subscription not found in this database"
                                  : `${z.belongs_to.replace(/_/g, " ")} renewal`}
                            </span>
                            {z.stripe?.billing_reason && ` · ${z.stripe.billing_reason}`}
                            {z.stripe?.line_count === 0 && " · no line items"}
                            {z.stripe?.total != null && ` · total ${fmt$(z.stripe.total)}`}
                            {z.stripe?.discounted && " · discounted"}
                            {z.stripe?.livemode === false && " · test mode"}
                            {z.stripe?.unavailable && ` · ${z.stripe.reason}`}
                            <span className="font-mono opacity-60"> · {z.stripe_invoice_id || "no invoice id"}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {isFan && hasFan && (
                  <div className="ml-5 mt-1 space-y-0.5 text-[11px] text-[#6B6A68]">
                    <div className="flex items-center gap-2">
                      <span className="opacity-60">└</span>
                      <span>creator amount · all supporter payments · gone at charge time</span>
                      <span className="flex-1" />
                      <span className="font-mono tabular-nums">{fmt$(tm.fan_routed_to_creator_cents)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="opacity-60">└</span>
                      <span>platform fees collected · all supporter payments · {(tm.fan_cut_bps_used / 100).toFixed(1)}%</span>
                      <span className="flex-1" />
                      <span className="font-mono tabular-nums text-[#2F6B3A]">+{fmt$(tm.application_fee_cents)}</span>
                    </div>
                    {tm.first_period_dedupe_cents > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="opacity-60">└</span>
                        <span>first-period duplicates removed · counted once now</span>
                        <span className="flex-1" />
                        <span className="font-mono tabular-nums">−{fmt$(tm.first_period_dedupe_cents)}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {/* If we have application_fee_cents but no fan_subscription row
              (e.g. tip-only fee), render a standalone summary line. */}
          {!hasFan && tm.application_fee_cents > 0 && (
            <div className="flex items-center gap-3 pt-2 mt-2 border-t hairline">
              <div className="w-2 h-2 rounded-full shrink-0" style={{ background: "#7B9E7E" }} />
              <div className="text-xs flex-1 min-w-0 text-[#1C1B1A]">
                Platform application fee
                <span className="text-[#6B6A68] ml-2">
                  · {(tm.fan_cut_bps_used / 100).toFixed(1)}% of {fmt$(tm.application_fee_source_cents)}
                </span>
              </div>
              <div className="text-xs font-mono tabular-nums text-[#2F6B3A]">+{fmt$(tm.application_fee_cents)}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function OwedBreakdown({ tm }) {
  const rows = tm.owed_by_user || [];
  return (
    <div className="mt-5 bg-white border hairline rounded-lg" data-testid="breakdown-owed">
      <div className="px-5 py-3 border-b hairline flex items-center gap-2">
        <div className="text-xs uppercase tracking-[0.22em] text-[#6B6A68]">Owed-to-users · breakdown</div>
        <span className="ml-auto text-[11px] text-[#6B6A68]">
          {rows.length} recipient{rows.length === 1 ? "" : "s"} this month
        </span>
      </div>
      {rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-[#6B6A68] italic">
          No rev-share owed yet this month.
        </div>
      ) : (
        <div className="divide-y hairline max-h-[280px] overflow-y-auto">
          {rows.map((r) => (
            <div key={r.user_id} className="px-5 py-2.5 flex items-center gap-3 text-xs" data-testid={`owed-row-${r.user_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-[#1C1B1A] truncate">
                  {r.name || r.email.split("@")[0]}
                  <span className="text-[#6B6A68] ml-2">· {r.email}</span>
                </div>
                <div className="text-[10px] text-[#6B6A68]">
                  {r.row_count} payout row{r.row_count === 1 ? "" : "s"} written this month
                </div>
              </div>
              <div className="font-mono tabular-nums text-[#1C1B1A]">{fmt$(r.total_cents)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function KeepBreakdown({ tm }) {
  // Reconstruct the platform-keep math line by line:
  //   + creator subscriptions (kept 100%)
  //   + affiliate gates (kept 100%)
  //   + application fees on fan tx (kept 5%)
  //   − rev-share owed to affiliates
  //   = platform keep
  const creatorSub = (tm.revenue_by_kind?.platform_subscription?.total_cents || 0)
    + (tm.platform_renewal_gross_cents || 0);
  const gates = tm.revenue_by_kind?.affiliate_gate?.total_cents || 0;
  const appFee = tm.application_fee_cents || 0;
  const owed = tm.owed_to_affiliates_cents || 0;
  return (
    <div className="mt-5 bg-white border hairline rounded-lg p-5 space-y-2 text-xs" data-testid="breakdown-keep">
      <div className="text-xs uppercase tracking-[0.22em] text-[#6B6A68] mb-1">Platform keep · breakdown</div>
      <KeepRow label="Creator subscriptions kept" value={creatorSub} sign="+" />
      <KeepRow label="Creator Pro purchases kept" value={gates} sign="+" />
      <KeepRow label={`Application fee on fan tx · ${(tm.fan_cut_bps_used / 100).toFixed(1)}%`} value={appFee} sign="+" />
      <KeepRow label="Rev-share owed to Partners" value={owed} sign="−" tone="alert" />
      <div className="pt-2 mt-2 border-t hairline flex items-center gap-3">
        <div className="flex-1 text-[#1C1B1A] font-medium">Platform keep this month</div>
        <div className="font-mono tabular-nums font-medium text-[#1C1B1A]">{fmt$(tm.platform_keep_cents)}</div>
      </div>
      {/* Sep 25, 2026 — this total is fees COLLECTED. It was being read as
          profit because nothing below it existed. */}
      <div className="pt-2 mt-2 border-t hairline space-y-2">
        <KeepRow label="Stripe's cut · processing, payout and per-account fees"
          value={tm.stripe_cost?.all_platform_cents} sign="−" tone="alert" />
        <div className="pt-2 border-t hairline flex items-center gap-3">
          <div className="flex-1 text-[#1C1B1A] font-medium">Platform keep after Stripe</div>
          <div className="font-mono tabular-nums font-medium"
            style={{ color: (tm.platform_keep_after_stripe_cents || 0) >= 0 ? "#2F6B3A" : "#7A3A2A" }}
            data-testid="keep-after-stripe">{fmt$(tm.platform_keep_after_stripe_cents)}</div>
        </div>
      </div>
    </div>
  );
}

function KeepRow({ label, value, sign, tone }) {
  const color = tone === "alert" ? "#C16E5A" : "#1C1B1A";
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 text-[#1C1B1A]">{label}</div>
      <div className="font-mono tabular-nums" style={{ color }}>{sign}{fmt$(value)}</div>
    </div>
  );
}

// ============== Small reusable bits ===================================
function StatusPill({ status }) {
  const map = {
    earned:  { bg: "#E8EFE9", fg: "#3A4F40", label: "earned" },
    held:    { bg: "#F4EEDC", fg: "#6B5A2D", label: "held" },
    pending: { bg: "#E7E1F5", fg: "#4B3A7A", label: "pending" },
  };
  const m = map[status] || { bg: "#F0F1EE", fg: "#6B6A68", label: status };
  return (
    <span
      className="text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded whitespace-nowrap"
      style={{ background: m.bg, color: m.fg }}
    >
      {m.label}
    </span>
  );
}

function KpiCard({ icon: Icon, label, sublabel, value, accent, big, testid, active, onClick }) {
  const clickable = typeof onClick === "function";
  const base = "border hairline rounded-lg p-4 transition-shadow";
  const interactive = clickable ? "cursor-pointer hover:shadow-md" : "";
  const activeRing = active ? "ring-2 ring-offset-1" : "";
  return (
    <div
      className={`bg-white ${base} ${interactive} ${activeRing}`}
      style={active ? { ["--tw-ring-color"]: accent } : undefined}
      onClick={onClick}
      data-testid={testid}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={14} weight="duotone" style={{ color: accent }} />
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">{label}</div>
      </div>
      <div className={`font-serif-display ${big ? "text-3xl" : "text-2xl"} tabular-nums`} style={{ color: accent }}>
        {value}
      </div>
      <div className="text-[10px] text-[#6B6A68] mt-1 leading-relaxed">{sublabel}</div>
    </div>
  );
}

function LedgerRow({ row }) {
  const isOutflow = row.kind === "cashout";
  const sign = isOutflow ? "−" : "+";
  const color = isOutflow ? "#7A3A2A" : "#2F6B3A";
  const userBit = row.user?.email || row.to_user?.email || row.from_email || "unknown";
  return (
    <div className="px-4 py-2.5 flex items-center gap-3 text-xs" data-testid={`ledger-row-${row.id}`}>
      <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: KIND_COLOR[row.kind] || "#6B6A68" }} />
      <div className="flex-1 min-w-0">
        <div className="text-[#1C1B1A] truncate">
          {KIND_LABEL[row.kind] || row.kind}
          <span className="text-[#6B6A68] ml-2">· {userBit}</span>
        </div>
        <div className="text-[10px] text-[#6B6A68]">
          {new Date(row.created_at).toLocaleString(undefined, {
            month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
          })}
        </div>
        {row.settled_at && (
          <div
            className="text-[10px] font-mono tabular-nums text-[#6B6A68] mt-0.5"
            data-testid={`ledger-settlement-${row.id}`}
          >
            Stripe {fmt$(row.stripe_fee_cents || 0)}
            {" · "}
            <span style={{ color: (row.platform_net_cents || 0) < 0 ? "#9B2C2C" : "#2F6B3A" }}>
              {(row.platform_net_cents || 0) < 0 ? "lost" : "kept"}{" "}
              {fmt$(Math.abs(row.platform_net_cents || 0))}
            </span>
            {row.creator_net_cents != null && ` · creator ${fmt$(row.creator_net_cents)}`}
          </div>
        )}
      </div>
      <div className="text-xs font-mono tabular-nums shrink-0" style={{ color }}>
        {sign}{fmt$(Math.abs(row.amount_cents || 0))}
      </div>
    </div>
  );
}


// ============== LLM cost per provider =================================
// Jun 2026 (Randy) — provider comes from each usage row's model_name.
// "Platform Key" is a key_source, NOT a provider: the Revenue / Cost /
// Margin columns MEASURE billable platform-key usage, so it never renders
// as a fourth provider row. Creator BYOK spend rides as a grey sub-line —
// visible, but excluded from platform revenue, cost and margin because the
// creator pays their own provider directly.
const PROVIDER_LABEL = {
  gemini: "Gemini", anthropic: "Anthropic", openai: "OpenAI", other: "Unattributed",
};
const PROVIDER_DOT = {
  gemini: "#4A5D4E", anthropic: "#C16E5A", openai: "#3A4A3E", other: "#6B6A68",
};

function LlmProviderRows({ rows, revenueCents, costCents, byokCents }) {
  if (!rows?.length) {
    return (
      <div
        className="mt-3 bg-white border hairline rounded-lg p-5 text-sm text-[#6B6A68] italic"
        data-testid="llm-by-provider-empty"
      >
        No model calls logged in the last 30 days.
      </div>
    );
  }
  const margin = (revenueCents || 0) - (costCents || 0);
  const marginPct = revenueCents > 0 ? Math.round((margin / revenueCents) * 1000) / 10 : null;
  return (
    <div className="mt-3 bg-white border hairline rounded-lg overflow-hidden" data-testid="llm-by-provider">
      <div className="px-4 py-2.5 border-b hairline flex items-baseline gap-2 flex-wrap">
        <div className="text-xs uppercase tracking-[0.22em] text-[#6B6A68]">LLM cost per provider</div>
        <span className="ml-auto text-[10px] text-[#6B6A68] italic">
          revenue / cost / margin count billable platform-key usage only
        </span>
      </div>
      <div className="px-4 py-1.5 flex items-center gap-2 text-[9px] uppercase tracking-[0.15em] text-[#6B6A68] bg-[#F7F6F1] border-b hairline">
        <span className="flex-1">Provider</span>
        <span className="w-20 text-right">Revenue</span>
        <span className="w-20 text-right">Est. cost</span>
        <span className="w-20 text-right">Margin</span>
        <span className="w-14 text-right">Margin %</span>
      </div>
      {rows.map((p) => (
        <div key={p.provider} className="border-b hairline" data-testid={`llm-provider-${p.provider}`}>
          <div className="px-4 py-2 flex items-center gap-2 text-[11px]">
            <span
              className="w-2 h-2 rounded-full shrink-0"
              style={{ background: PROVIDER_DOT[p.provider] || PROVIDER_DOT.other }}
            />
            <span className="flex-1 min-w-0 text-[#1C1B1A] truncate">
              {PROVIDER_LABEL[p.provider] || p.provider}
              <span className="text-[#6B6A68] ml-1.5">
                · {(p.billable_calls || 0).toLocaleString()} billable of {(p.calls || 0).toLocaleString()} calls
              </span>
            </span>
            <span className="w-20 text-right font-mono tabular-nums text-[#1C1B1A]">{fmt$(p.revenue_cents)}</span>
            <span className="w-20 text-right font-mono tabular-nums text-[#2F6B3A]">{fmt$(p.cost_cents)}</span>
            <span className="w-20 text-right font-mono tabular-nums text-[#7A5200]">{fmt$(p.margin_cents)}</span>
            <span
              className="w-14 text-right font-mono tabular-nums text-[#6B6A68]"
              data-testid={`llm-provider-pct-${p.provider}`}
            >
              {p.margin_pct == null ? "—" : `${p.margin_pct}%`}
            </span>
          </div>
          <div className="px-4 pb-2 space-y-0.5 text-[10px] text-[#6B6A68]">
            {(p.byok_cents || 0) > 0 && (
              <div className="flex items-center gap-1.5" data-testid={`llm-provider-byok-${p.provider}`}>
                <span className="opacity-50 ml-3">└</span>
                <span>creator-funded BYOK · {(p.byok_calls || 0).toLocaleString()} calls ·</span>
                <span className="font-mono tabular-nums">{fmt$(p.byok_cents)}</span>
                <span className="italic opacity-70">not billed by us</span>
              </div>
            )}
            {(p.models || []).map((m) => (
              <div
                key={m.model_name}
                className="flex items-center gap-1.5"
                data-testid={`llm-provider-model-${m.model_name}`}
              >
                <span className="opacity-30 ml-3">·</span>
                <span className="font-mono">{m.model_name}</span>
                <span className="opacity-60">
                  {(m.billable_calls || 0).toLocaleString()} billable of {(m.calls || 0).toLocaleString()} calls
                </span>
                <span className="font-mono tabular-nums">{fmt$(m.cents)}</span>
                <span className="opacity-50">total spend</span>
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="px-4 py-2 flex items-center gap-2 text-[11px] bg-[#F7F6F1]" data-testid="llm-provider-rollup">
        <span className="w-2 h-2 rounded-full shrink-0 bg-[#1C1B1A]" />
        <span className="flex-1 text-[#1C1B1A]">
          Platform Key roll-up
          <span className="text-[#6B6A68] ml-1.5">· key source, not a provider</span>
        </span>
        <span className="w-20 text-right font-mono tabular-nums text-[#1C1B1A]">{fmt$(revenueCents)}</span>
        <span className="w-20 text-right font-mono tabular-nums text-[#2F6B3A]">{fmt$(costCents)}</span>
        <span className="w-20 text-right font-mono tabular-nums text-[#7A5200]">{fmt$(margin)}</span>
        <span className="w-14 text-right font-mono tabular-nums text-[#6B6A68]">
          {marginPct == null ? "—" : `${marginPct}%`}
        </span>
      </div>
      {(byokCents || 0) > 0 && (
        <div className="px-4 py-2 text-[10px] text-[#6B6A68] border-t hairline" data-testid="llm-provider-byok-rollup">
          Creator-funded BYOK across all providers · {fmt$(byokCents)} — their provider invoice, not ours.
        </div>
      )}
    </div>
  );
}

